/**
 * The checkout form's state transitions, as a pure function.
 *
 * WHY A REDUCER, and not the 20 useState calls this replaces: the bugs in this form were
 * transition bugs, not value bugs. Three separate useEffects existed to express "when X
 * changes, Y must also change" -- and each one had a defect that came directly from that
 * shape:
 *
 *   - visibility reconciliation needed a ref + a 500ms timer to tell a user's click apart
 *     from a URL hydration (see RECONCILIATION below);
 *   - error clearing listed `errors` in its own dependency array while calling setErrors;
 *   - autofill capture re-armed a timer on every keystroke.
 *
 * As transitions those relationships are atomic: PLAN_SELECTED deselects newly-invisible
 * addons in the SAME dispatch, so there is no intermediate render in which the state is
 * inconsistent, and nothing needs to guess how long a batch of setState calls will take.
 *
 * No React import. That is deliberate -- it makes every rule below testable as plain data in
 * and data out, with no renderer, no jsdom, and no act() wrapper.
 *
 * DERIVED STATE IS NOT STORED. The cart, the visible-item lists, and "is the form
 * submittable" are all computed from this state by useCheckoutForm. Storing them would
 * create a second source of truth that could fall out of sync -- which is precisely the
 * class of bug Phase 2 removed from pricing.
 */

import { isItemVisible } from '../../lib/pricing/visibility.js';
// Explicit .js extension: webpack and Jest resolve the bare path, but bare Node (used to
// smoke-test reconciliation against the real catalog) requires fully-specified imports.
import { CUSTOMER_FIELD_NAMES, emptyCustomer } from './checkoutSchema.js';

export const ACTIONS = {
  FIELD_CHANGED: 'FIELD_CHANGED',
  FIELD_BLURRED: 'FIELD_BLURRED',
  AUTOFILL_DETECTED: 'AUTOFILL_DETECTED',
  PLAN_SELECTED: 'PLAN_SELECTED',
  TERM_SELECTED: 'TERM_SELECTED',
  ADDON_TOGGLED: 'ADDON_TOGGLED',
  ONETIME_TOGGLED: 'ONETIME_TOGGLED',
  QUESTION_ANSWERED_RADIO: 'QUESTION_ANSWERED_RADIO',
  QUESTION_ANSWERED_CHECKBOX: 'QUESTION_ANSWERED_CHECKBOX',
  TERMS_TOGGLED: 'TERMS_TOGGLED',
  MAILCHIMP_TOGGLED: 'MAILCHIMP_TOGGLED',
  COUPON_CODE_CHANGED: 'COUPON_CODE_CHANGED',
  COUPON_FIELD_EXPANDED: 'COUPON_FIELD_EXPANDED',
  COUPON_FIELD_CANCELLED: 'COUPON_FIELD_CANCELLED',
  COUPON_VALIDATING: 'COUPON_VALIDATING',
  COUPON_APPLIED: 'COUPON_APPLIED',
  COUPON_REJECTED: 'COUPON_REJECTED',
  COUPON_REMOVED: 'COUPON_REMOVED',
  HYDRATED: 'HYDRATED',
  RESUME_DISMISSED: 'RESUME_DISMISSED',
  STALE_LINK_DISMISSED: 'STALE_LINK_DISMISSED',
  SUBMIT_STARTED: 'SUBMIT_STARTED',
  SUBMIT_BLOCKED: 'SUBMIT_BLOCKED',
  SUBMIT_FAILED: 'SUBMIT_FAILED',
  SUBMIT_SETTLED: 'SUBMIT_SETTLED',
};

/**
 * The catalog the reducer reconciles against.
 *
 * Injected rather than imported so reducer tests can use small fixtures instead of the real
 * product config -- a test for "changing plan deselects a now-invisible addon" should not
 * break because marketing edited a visibility rule.
 */
export function makeInitialState(catalog, overrides = {}) {
  return {
    customer: emptyCustomer(),
    selection: {
      primaryPlanId: 'business-plan',
      term: 'monthly',
      addonIds: {},
      oneTimeIds: {},
      questionAnswers: {},
    },
    coupon: {
      code: '',
      applied: null,
      error: '',
      expanded: false,
      validating: false,
    },
    consent: {
      agreedToTerms: false,
      mailchimpOptIn: true, // opt-out model: checked by default
    },
    ui: {
      errors: {},
      submitting: false,
      submitError: null,
      showResumeMessage: false,
      /**
       * URL params that named a plan/addon/service we could not resolve. Previously these
       * produced a console.warn and nothing else, so an account manager whose shared link had
       * gone stale saw the default plan and had no idea their quote was being misrepresented.
       */
      unresolvedParams: [],
    },
    catalog,
    ...overrides,
  };
}

/**
 * RECONCILIATION -- the rule the deleted useEffect was trying to express.
 *
 * When the plan or term changes, some addons/one-time items may no longer be purchasable.
 * Leaving them selected means the customer sees a line item they will not be charged for
 * (or, before Phase 2, one they WOULD be charged for that the UI had already hidden).
 *
 * Doing this inside the transition is what removes `isLoadingFromUrlRef` and its 500ms
 * timer. The old effect fired on ANY plan/term change and could not distinguish a user's
 * click from a URL hydration, so it would strip selections the shared link had explicitly
 * requested. The ref suppressed it for 500ms and hoped every setState had flushed by then --
 * a race with no correctness argument behind it. Here, PLAN_SELECTED knows it is a user
 * action and HYDRATED knows it is a restore; each reconciles against its own final
 * plan/term, atomically, and there is no window in between for anything to clobber.
 *
 * Note this SETS FALSE rather than deleting keys, matching the previous behavior exactly --
 * resolveCart treats falsy as unselected, and preserving the key means re-selecting the
 * original plan does not resurrect a stale `true`.
 */
function reconcileSelection(selection, catalog) {
  const ctx = { term: selection.term, primaryPlanId: selection.primaryPlanId };

  // A primary plan that is no longer visible falls back to the first visible one, mirroring
  // the old effect. An empty catalog yields '' and the form shows its "select a plan" notice.
  const primaryVisible = (catalog.primaryProducts || []).filter((p) => isItemVisible(p, ctx));
  const primaryPlanId =
    selection.primaryPlanId && !primaryVisible.some((p) => p.id === selection.primaryPlanId)
      ? primaryVisible[0]?.id || ''
      : selection.primaryPlanId;

  // Re-derive context: if the plan itself was just replaced, visibility for addons must be
  // judged against the REPLACEMENT, not the plan that just disappeared.
  const finalCtx = { term: selection.term, primaryPlanId };

  const dropInvisible = (selected, items) => {
    const byId = new Map((items || []).map((item) => [item.id, item]));
    let changed = false;
    const next = { ...selected };

    Object.keys(next).forEach((id) => {
      const item = byId.get(id);
      if (next[id] && (!item || !isItemVisible(item, finalCtx))) {
        next[id] = false;
        changed = true;
      }
    });

    // Return the original object when nothing changed so React can skip re-rendering
    // consumers memoized on it.
    return changed ? next : selected;
  };

  return {
    ...selection,
    primaryPlanId,
    addonIds: dropInvisible(selection.addonIds, catalog.addonSubscriptions),
    oneTimeIds: dropInvisible(selection.oneTimeIds, catalog.oneTimeProducts),
  };
}

/** Drop one key from the errors map, returning the original object if it was absent. */
function withoutError(errors, field) {
  if (!(field in errors)) return errors;
  const next = { ...errors };
  delete next[field];
  return next;
}

export function checkoutReducer(state, action) {
  switch (action.type) {
    /**
     * Clear-on-valid, set-never. Introducing an error mid-typing would flag "r" as an invalid
     * email while someone is still typing "renee@..."; validation on blur is what reports
     * problems. This is the first half of the deleted error-clearing effect, now synchronous
     * with the change that causes it.
     */
    case ACTIONS.FIELD_CHANGED: {
      const { field, value } = action;
      const hadError = Boolean(state.ui.errors[field]);
      const nowValid = hadError && !action.validate(field, value);

      return {
        ...state,
        customer: { ...state.customer, [field]: value },
        ui: nowValid
          ? { ...state.ui, errors: withoutError(state.ui.errors, field) }
          : state.ui,
      };
    }

    /** The other half: validate when the user has finished with the field. */
    case ACTIONS.FIELD_BLURRED: {
      const { field, value } = action;
      const message = action.validate(field, value);

      return {
        ...state,
        ui: {
          ...state.ui,
          errors: message
            ? { ...state.ui.errors, [field]: message }
            : withoutError(state.ui.errors, field),
        },
      };
    }

    /**
     * Values the browser painted into inputs before React hydrated. Only non-empty DOM values
     * are adopted, and only for fields the user has not already filled -- so this can never
     * overwrite typed input or a URL prefill.
     */
    case ACTIONS.AUTOFILL_DETECTED: {
      const adopted = {};
      CUSTOMER_FIELD_NAMES.forEach((name) => {
        const value = action.values[name];
        if (typeof value === 'string' && value && !state.customer[name]) {
          adopted[name] = value;
        }
      });

      if (Object.keys(adopted).length === 0) return state;
      return { ...state, customer: { ...state.customer, ...adopted } };
    }

    case ACTIONS.PLAN_SELECTED:
      if (action.planId === state.selection.primaryPlanId) return state;
      return {
        ...state,
        selection: reconcileSelection(
          { ...state.selection, primaryPlanId: action.planId },
          state.catalog
        ),
      };

    case ACTIONS.TERM_SELECTED:
      if (action.term === state.selection.term) return state;
      return {
        ...state,
        selection: reconcileSelection(
          { ...state.selection, term: action.term },
          state.catalog
        ),
      };

    case ACTIONS.ADDON_TOGGLED:
      return {
        ...state,
        selection: {
          ...state.selection,
          addonIds: {
            ...state.selection.addonIds,
            [action.id]: !state.selection.addonIds[action.id],
          },
        },
      };

    case ACTIONS.ONETIME_TOGGLED:
      return {
        ...state,
        selection: {
          ...state.selection,
          oneTimeIds: {
            ...state.selection.oneTimeIds,
            [action.id]: !state.selection.oneTimeIds[action.id],
          },
        },
      };

    case ACTIONS.QUESTION_ANSWERED_RADIO:
      return {
        ...state,
        selection: {
          ...state.selection,
          questionAnswers: {
            ...state.selection.questionAnswers,
            [action.questionId]: action.answerId,
          },
        },
      };

    /**
     * Checkbox answers are an { answerId: bool } map. The defensive isObject check is
     * preserved from the original: a question whose type changed from radio to checkbox can
     * find a bare string left in a saved localStorage blob or a shared URL, and spreading a
     * string would produce { 0: 'y', 1: 'e', ... }.
     */
    case ACTIONS.QUESTION_ANSWERED_CHECKBOX: {
      const current = state.selection.questionAnswers[action.questionId];
      const isObject = current && typeof current === 'object' && !Array.isArray(current);
      const answers = isObject ? { ...current } : {};

      return {
        ...state,
        selection: {
          ...state.selection,
          questionAnswers: {
            ...state.selection.questionAnswers,
            [action.questionId]: { ...answers, [action.answerId]: !answers[action.answerId] },
          },
        },
      };
    }

    /** Checking the box clears its error immediately -- the complaint is resolved. */
    case ACTIONS.TERMS_TOGGLED:
      return {
        ...state,
        consent: { ...state.consent, agreedToTerms: action.value },
        ui: action.value
          ? { ...state.ui, errors: withoutError(state.ui.errors, 'agreedToTerms') }
          : state.ui,
      };

    case ACTIONS.MAILCHIMP_TOGGLED:
      return { ...state, consent: { ...state.consent, mailchimpOptIn: action.value } };

    case ACTIONS.COUPON_CODE_CHANGED:
      return { ...state, coupon: { ...state.coupon, code: action.value } };

    case ACTIONS.COUPON_FIELD_EXPANDED:
      return { ...state, coupon: { ...state.coupon, expanded: true } };

    case ACTIONS.COUPON_FIELD_CANCELLED:
      return { ...state, coupon: { ...state.coupon, expanded: false, code: '', error: '' } };

    case ACTIONS.COUPON_VALIDATING:
      return { ...state, coupon: { ...state.coupon, validating: true, error: '' } };

    /** Collapse the field on success: the applied coupon is shown as a summary row instead. */
    case ACTIONS.COUPON_APPLIED:
      return {
        ...state,
        coupon: {
          ...state.coupon,
          applied: action.coupon,
          error: '',
          expanded: false,
          validating: false,
        },
      };

    case ACTIONS.COUPON_REJECTED:
      return {
        ...state,
        coupon: { ...state.coupon, applied: null, error: action.message, validating: false },
      };

    case ACTIONS.COUPON_REMOVED:
      return {
        ...state,
        coupon: { code: '', applied: null, error: '', expanded: false, validating: false },
      };

    /**
     * One atomic restore from localStorage or a share URL.
     *
     * This is the transition the old code could not express. Hydration arrives as a partial
     * patch, is merged, and is reconciled ONCE against its own plan/term -- so a shared link
     * for a yearly starter plan does not briefly reconcile against the default monthly
     * business plan and lose its addons. Because it happens in a single dispatch there is no
     * later pass to suppress, which is what makes the ref and the 500ms timer unnecessary
     * rather than merely tidied away.
     *
     * Reconciliation still RUNS here: a link written months ago may name items that are no
     * longer purchasable together, and honoring that would show a total Stripe will not
     * charge. Anything dropped is reported through unresolvedParams, not swallowed.
     */
    case ACTIONS.HYDRATED: {
      const merged = { ...state.selection, ...(action.selection || {}) };
      const reconciled = reconcileSelection(merged, state.catalog);

      // Report what reconciliation removed, so the UI can say the link is stale instead of
      // silently showing a different quote than the sender intended.
      const dropped = [];
      ['addonIds', 'oneTimeIds'].forEach((key) => {
        Object.keys(merged[key] || {}).forEach((id) => {
          if (merged[key][id] && !reconciled[key][id]) dropped.push(id);
        });
      });
      if (merged.primaryPlanId !== reconciled.primaryPlanId) {
        dropped.push(merged.primaryPlanId);
      }

      return {
        ...state,
        selection: reconciled,
        customer: { ...state.customer, ...(action.customer || {}) },
        coupon: { ...state.coupon, ...(action.coupon || {}) },
        consent: { ...state.consent, ...(action.consent || {}) },
        ui: {
          ...state.ui,
          showResumeMessage: Boolean(action.showResumeMessage),
          unresolvedParams: [...(action.unresolvedParams || []), ...dropped],
        },
      };
    }

    case ACTIONS.RESUME_DISMISSED:
      return { ...state, ui: { ...state.ui, showResumeMessage: false } };

    case ACTIONS.STALE_LINK_DISMISSED:
      return { ...state, ui: { ...state.ui, unresolvedParams: [] } };

    case ACTIONS.SUBMIT_STARTED:
      return { ...state, ui: { ...state.ui, submitting: true, submitError: null } };

    /** Client-side validation failed. Errors are the full replacement set, as before. */
    case ACTIONS.SUBMIT_BLOCKED:
      return {
        ...state,
        ui: { ...state.ui, submitting: false, errors: action.errors, submitError: null },
      };

    /**
     * The server rejected the request. Field-keyed errors land on their own fields; the
     * message goes to submitError, which renders inline. This replaces two alert() calls --
     * a modal that cannot be styled, announced, or dismissed by keyboard on a payment page.
     */
    case ACTIONS.SUBMIT_FAILED:
      return {
        ...state,
        ui: {
          ...state.ui,
          submitting: false,
          submitError: action.message,
          errors: { ...state.ui.errors, ...(action.errors || {}) },
        },
      };

    case ACTIONS.SUBMIT_SETTLED:
      return { ...state, ui: { ...state.ui, submitting: false } };

    default:
      // Loud in development, inert in production: an unknown action is a programming error,
      // but throwing on a live checkout page would white-screen it.
      if (process.env.NODE_ENV !== 'production') {
        console.error(`checkoutReducer: unknown action type "${action?.type}"`);
      }
      return state;
  }
}
